import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { GeoJSON, MapContainer, TileLayer, useMap } from "react-leaflet";

const OSM_STANDARD_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const CARTO_LIGHT_URL = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const FULL_GEOJSON_URL = "/map_runtime/data/geo_clusters_2024.geojson";

const CLUSTERS = {
  HH: { color: "#ef553b", title: "Высокий рядом с высоким", text: "Устойчивые зоны высокого прогнозного риска, окружённые другими высокорисковыми H3. Это приоритетные территории для детальной инженерной проверки." },
  LL: { color: "#636efa", title: "Низкий рядом с низким", text: "Крупные связные территории низкого прогнозного риска. Они полезны как сравнительная группа, но не означают нулевой риск." },
  LH: { color: "#00cc96", title: "Низкий среди высоких соседей", text: "Локальные пространственные выбросы: отдельный H3 имеет низкий риск среди высокорисковых соседей. Такие зоны требуют осторожной интерпретации." },
};

const CLUSTER_SUMMARY = [
  { code: "HH", allHexagons: 693, significantHexagons: 693 },
  { code: "ISLAND", allHexagons: 13, significantHexagons: null },
  { code: "LH", allHexagons: 98, significantHexagons: 98 },
  { code: "LL", allHexagons: 1894, significantHexagons: 1894 },
  { code: "NS", allHexagons: 103819, significantHexagons: null },
];

const CLUSTER_COMPOSITION = [
  { code: "LL", value: 1894, color: CLUSTERS.LL.color },
  { code: "HH", value: 693, color: CLUSTERS.HH.color },
  { code: "LH", value: 98, color: CLUSTERS.LH.color },
];

function RemoveLeafletAttribution() {
  const map = useMap();
  useEffect(() => { map.attributionControl?.remove(); }, [map]);
  return null;
}

function ClusterMap() {
  const [mapState, setMapState] = useState({ loading: true, error: "", data: null });
  const [tileUrl, setTileUrl] = useState(OSM_STANDARD_URL);

  useEffect(() => {
    let cancelled = false;
    fetch(FULL_GEOJSON_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (data?.features?.length !== 2685) throw new Error("Unexpected feature count");
        if (!cancelled) setMapState({ loading: false, error: "", data });
      })
      .catch(() => { if (!cancelled) setMapState({ loading: false, error: "Не удалось загрузить витрину локальных кластеров.", data: null }); });
    return () => { cancelled = true; };
  }, []);

  const style = (feature) => {
    const cluster = CLUSTERS[feature.properties.predicted_cluster_code];
    return { color: "#334155", weight: 0.22, fillColor: cluster?.color || "#94a3b8", fillOpacity: 0.78 };
  };

  const bindTooltip = (feature, layer) => {
    const properties = feature.properties;
    const code = properties.predicted_cluster_code;
    layer.bindTooltip(`<strong>H3: ${properties.hex_id}</strong><br>Кластер: ${code} — ${CLUSTERS[code]?.title || code}<br>Прогнозное число ДТП: ${Number(properties.predicted_accident_count).toFixed(4)}<br>Фактические ДТП: ${properties.observed_accident_count}<br>Смертельные ДТП: ${properties.observed_fatal_accident_count}`, { sticky: true, direction: "top", opacity: 0.96 });
  };

  if (mapState.loading) return <div className="statistics-geo-clusters-map-state">Загружаем все 2 685 значимых H3…</div>;
  if (mapState.error) return <div className="statistics-geo-clusters-map-state is-error">{mapState.error}</div>;

  return <MapContainer className="statistics-geo-clusters-map" center={[55.75, 37.62]} zoom={9.5} minZoom={8} scrollWheelZoom attributionControl={false}>
    <RemoveLeafletAttribution />
    <TileLayer url={tileUrl} eventHandlers={{ tileerror: () => setTileUrl((current) => current === OSM_STANDARD_URL ? CARTO_LIGHT_URL : current) }} />
    <GeoJSON data={mapState.data} style={style} onEachFeature={bindTooltip} />
  </MapContainer>;
}

export default function GeoClustersReportSection() {
  const significantCount = useMemo(() => CLUSTER_COMPOSITION.reduce((total, row) => total + row.value, 0), []);

  return <section className="statistics-geo-clusters-report">
    <header className="statistics-geo-clusters-hero">
      <h2>Локальные пространственные кластеры Local Moran</h2>
      <p>Карта показывает все H3, которые сохранили значимость после FDR для прогнозного риска. Отдельная проверка остатков модели не выявила ни одного значимого локального кластера после FDR.</p>
    </header>

    <div className="statistics-geo-clusters-summary-grid">
      <article><span>Значимые H3</span><strong>{significantCount.toLocaleString("ru-RU")}</strong><small>После FDR</small></article>
      <article><span>HH</span><strong>693</strong><small>Высокий рядом с высоким</small></article>
      <article><span>LL</span><strong>1 894</strong><small>Низкий рядом с низким</small></article>
      <article><span>LH</span><strong>98</strong><small>Низкий среди высоких соседей</small></article>
      <article><span>Значимые остаточные</span><strong>0</strong><small>После FDR</small></article>
    </div>

    <div className="statistics-geo-clusters-map-heading"><h3>Карта значимых кластеров</h3><small>Подложка OpenStreetMap требует интернет-соединения</small></div>
    <div className="statistics-geo-clusters-map-shell"><ClusterMap /></div>

    <div className="statistics-report-section-heading"><h3>Структура кластеров</h3></div>
    <div className="statistics-report-chart-grid">
      <section className="statistics-report-card"><h3>Число H3 по типу локального кластера</h3><ResponsiveContainer width="100%" height={390}><BarChart data={CLUSTER_SUMMARY} margin={{ top: 22, right: 18, bottom: 20, left: 8 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" /><XAxis dataKey="code" tick={{ fontSize: 11 }} /><YAxis scale="log" domain={[10, 150000]} tick={{ fontSize: 10 }} tickFormatter={(value) => value >= 1000 ? `${Math.round(value / 1000)}k` : value} label={{ value: "H3, логарифмическая шкала", angle: -90, position: "insideLeft", fontSize: 11 }} /><RechartsTooltip formatter={(value) => Number(value).toLocaleString("ru-RU")} /><Legend verticalAlign="top" height={28} /><Bar dataKey="allHexagons" name="Все H3" fill="#636efa" /><Bar dataKey="significantHexagons" name="Значимые после FDR" fill="#ef553b" />
      </BarChart></ResponsiveContainer></section>
      <section className="statistics-report-card"><h3>Состав 2 685 значимых кластеров</h3><ResponsiveContainer width="100%" height={390}><PieChart><Legend verticalAlign="top" height={28} /><RechartsTooltip formatter={(value) => Number(value).toLocaleString("ru-RU")} /><Pie data={CLUSTER_COMPOSITION} dataKey="value" nameKey="code" cx="50%" cy="55%" innerRadius={75} outerRadius={125} label={({ name, value, percent }) => `${name}\n${value}\n${(percent * 100).toFixed(1)}%`}>{CLUSTER_COMPOSITION.map((row) => <Cell key={row.code} fill={row.color} />)}</Pie></PieChart></ResponsiveContainer></section>
    </div>

    <div className="statistics-report-section-heading"><h3>Интерпретация кластеров</h3></div>
    <div className="statistics-geo-clusters-interpretation-grid">{Object.entries(CLUSTERS).map(([code, cluster]) => <article key={code}><h3>{code}</h3><p>{cluster.text}</p></article>)}</div>
    <p className="statistics-geo-risk-source-note">Источник: полный GeoJSON, извлечённый из <code>temp_reposit/DTP_STATIC_HTML_REPORT_V2/08_geo_clusters.html</code>. Карта загружается только при открытии вкладки.</p>
  </section>;
}
